/**
 * Takeovers analytics — pipeline KPIs, status distribution, requests per
 * creator, and budget-tier mix. Uses the same lazy-expiry rule as the list
 * (computed server-side in the summary endpoint).
 */

import React, { useEffect, useState } from 'react';
import { EmptyState, KpiCard, Spinner, tableStyles } from '../ui';
import { getTakeoverAnalytics } from '../../services/takeoversApi';
import { fmtUsd } from './shared';
import { STATUS_LABELS, TAKEOVER_STATUSES, TakeoverAnalyticsSummary } from './types';

const STATUS_COLORS: Record<string, string> = {
  new: 'var(--warning)',
  in_review: 'var(--cg-plum-400)',
  approved: 'var(--success)',
  booked: 'var(--accent)',
  declined: 'var(--cg-neutral-400)',
  expired: 'var(--danger)',
};

const AnalyticsPanel: React.FC = () => {
  const [summary, setSummary] = useState<TakeoverAnalyticsSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    getTakeoverAnalytics()
      .then((s) => !cancelled && setSummary(s))
      .catch((err) => !cancelled && setError(err?.message || 'Failed to load analytics'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; };
  }, []);

  if (loading) return <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><Spinner /></div>;
  if (error) return <div style={{ color: 'var(--danger)', padding: 20 }}>{error}</div>;
  if (!summary || summary.totalRequests === 0) {
    return <EmptyState title="No data yet" subtitle="Analytics appear once requests start coming in." />;
  }

  const booked = summary.statusCounts.booked ?? 0;
  const decided = booked + (summary.statusCounts.declined ?? 0) + (summary.statusCounts.expired ?? 0);
  const conversion = decided > 0 ? `${Math.round((booked / decided) * 100)}%` : '—';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14 }}>
        <KpiCard label="Total requests" value={summary.totalRequests} featured />
        <KpiCard label="Pipeline estimate" value={fmtUsd(summary.pipelineEstimateUsd)} hint="Tier midpoints, active requests only" />
        <KpiCard label="Booked" value={booked} />
        <KpiCard label="Conversion (of decided)" value={conversion} hint="Booked ÷ (booked + declined + expired)" />
      </div>

      <div>
        <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--fg-4)', marginBottom: 8 }}>
          Status distribution
        </div>
        <div style={{ display: 'flex', height: 14, borderRadius: 7, overflow: 'hidden', border: '1px solid var(--border-1)' }}>
          {TAKEOVER_STATUSES.filter((s) => (summary.statusCounts[s] ?? 0) > 0).map((s) => (
            <div
              key={s}
              title={`${STATUS_LABELS[s]}: ${summary.statusCounts[s]}`}
              style={{ flex: summary.statusCounts[s], background: STATUS_COLORS[s] }}
            />
          ))}
        </div>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', marginTop: 8 }}>
          {TAKEOVER_STATUSES.map((s) => (
            <span key={s} style={{ fontSize: 12, color: 'var(--fg-3)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
              <span style={{ width: 9, height: 9, borderRadius: '50%', background: STATUS_COLORS[s], display: 'inline-block' }} />
              {STATUS_LABELS[s]} · {summary.statusCounts[s] ?? 0}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(320px, 2fr) minmax(240px, 1fr)', gap: 16, alignItems: 'start' }}>
        <div style={tableStyles.wrap}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Creator</th>
                <th style={tableStyles.thNum}>Requests</th>
                <th style={tableStyles.thNum}>Booked</th>
                <th style={tableStyles.thNum}>In pipeline</th>
              </tr>
            </thead>
            <tbody>
              {summary.requestsPerCreator.map((c) => {
                const inPipeline =
                  (c.statusCounts.new ?? 0) + (c.statusCounts.in_review ?? 0) + (c.statusCounts.approved ?? 0);
                return (
                  <tr key={c.creatorId}>
                    <td style={tableStyles.td}>{c.creatorDisplayName}</td>
                    <td style={tableStyles.tdNum}>{c.total}</td>
                    <td style={tableStyles.tdNum}>{c.statusCounts.booked ?? 0}</td>
                    <td style={tableStyles.tdNum}>{inPipeline}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={tableStyles.wrap}>
          <table style={tableStyles.table}>
            <thead>
              <tr>
                <th style={tableStyles.th}>Budget tier</th>
                <th style={tableStyles.thNum}>Requests</th>
              </tr>
            </thead>
            <tbody>
              {summary.budgetTierCounts.map((t) => (
                <tr key={t.label}>
                  <td style={tableStyles.td}>{t.label}</td>
                  <td style={tableStyles.tdNum}>{t.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsPanel;
